import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { fetchHistory } from '../api/me';
import { fetchPuzzle, type Difficulty } from '../api/puzzles';
import { getLife } from '../api/lives';
import { fetchLeaderboard } from '../api/leaderboard';
import { Board } from '../components/board/Board';
import { formatDateUK } from '../lib/format';
import { percentileLabel } from '../lib/percentile';

const DIFFICULTIES = new Set(['EASY', 'MEDIUM', 'HARD']);

/** Read-only playback of a past life (spec 10.1 /history/:date/:difficulty). */
export default function Replay() {
  const params = useParams<{ date: string; difficulty: string }>();
  const date = params.date ?? '';
  const difficulty = (params.difficulty ?? '').toUpperCase() as Difficulty;
  const { user, loading } = useAuth();
  const [lifeIndex, setLifeIndex] = useState(0);
  const [step, setStep] = useState<number | null>(null); // null = latest move

  const livesQuery = useQuery({
    queryKey: ['me', 'history', date, difficulty],
    queryFn: () => fetchHistory({ from: date, to: date, difficulty }),
    enabled: !!user && DIFFICULTIES.has(difficulty),
  });
  const entries = livesQuery.data?.entries ?? [];
  const selected = entries[lifeIndex] ?? null;

  const puzzleQuery = useQuery({
    queryKey: ['puzzle', date, difficulty],
    queryFn: () => fetchPuzzle(date, difficulty),
    enabled: DIFFICULTIES.has(difficulty),
  });
  const lifeQuery = useQuery({
    queryKey: ['life', selected?.life_id],
    queryFn: () => getLife(selected!.life_id),
    enabled: !!selected,
  });

  // Per-puzzle standing (same board for everyone, so it's a fair ranking).
  const leaderboardQuery = useQuery({
    queryKey: ['leaderboard', date, difficulty],
    queryFn: () => fetchLeaderboard(date, difficulty),
    enabled: !!user && DIFFICULTIES.has(difficulty),
  });
  const standing = percentileLabel(
    leaderboardQuery.data?.me?.rank ?? null,
    leaderboardQuery.data?.total_players ?? 0,
  );

  const moves = lifeQuery.data?.moves ?? [];
  const shownStep = step ?? moves.length;

  const boardAtStep = useMemo(() => {
    const placed = [...(puzzleQuery.data?.starting_board.placed ?? [])];
    for (const move of moves.slice(0, shownStep)) {
      for (const p of move.placements) {
        placed.push({ row: p.row, col: p.col, letter: p.letter, is_blank: p.is_blank });
      }
    }
    return { placed };
  }, [puzzleQuery.data, moves, shownStep]);

  if (!DIFFICULTIES.has(difficulty)) {
    return (
      <section data-testid="page-replay">
        <p role="alert" className="text-red-600">
          Unknown difficulty.
        </p>
      </section>
    );
  }
  if (!loading && !user) {
    return (
      <section data-testid="page-replay">
        <h1 className="text-2xl font-bold">Replay</h1>
        <p className="mt-4 text-stone-600">
          <Link to="/login" className="font-semibold text-emerald-700 underline">
            Sign in
          </Link>{' '}
          to replay your past games.
        </p>
      </section>
    );
  }

  return (
    <section data-testid="page-replay">
      <Link
        to="/history"
        className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:underline"
      >
        <span aria-hidden="true">←</span> Back to history
      </Link>
      <h1 className="mt-2 text-lg font-bold">
        <span className="capitalize">{difficulty.toLowerCase()}</span> · {formatDateUK(date)} ·
        Replay
      </h1>

      {livesQuery.isLoading || puzzleQuery.isLoading ? (
        <p role="status" className="mt-4 text-stone-500">
          Loading replay…
        </p>
      ) : entries.length === 0 ? (
        <p className="mt-4 text-stone-600">You didn't play this puzzle.</p>
      ) : (
        <>
          {entries.length > 1 ? (
            <div role="group" aria-label="Choose a life" className="mt-3 flex gap-2">
              {entries.map((e, i) => (
                <button
                  key={e.life_id}
                  type="button"
                  aria-pressed={i === lifeIndex}
                  onClick={() => {
                    setLifeIndex(i);
                    setStep(null);
                  }}
                  className={`rounded-full px-3 py-1 text-sm font-medium ${
                    i === lifeIndex
                      ? 'bg-emerald-700 text-white'
                      : 'border border-stone-300 text-stone-600'
                  }`}
                >
                  Life {e.life_number} {e.won ? '✓' : e.status === 'LOST' ? '✗' : '…'}
                </button>
              ))}
            </div>
          ) : null}

          {selected ? (
            <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-stone-600">
              <span>
                {selected.won ? 'Won' : selected.status === 'LOST' ? 'Lost' : 'Unfinished'} ·{' '}
                {selected.final_score}/{selected.target_score} · {selected.moves_used} of{' '}
                {selected.move_count} moves
              </span>
              {standing ? (
                <span
                  data-testid="replay-standing"
                  title={standing.title}
                  className="inline-block rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800"
                >
                  {standing.text}
                </span>
              ) : null}
            </p>
          ) : null}

          <div className="mt-3">
            <Board board={boardAtStep} staged={[]} />
          </div>

          <div className="mt-3 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep(Math.max(0, shownStep - 1))}
              disabled={shownStep === 0}
              className="rounded-md border border-stone-300 px-3 py-2 text-sm font-medium disabled:opacity-40"
            >
              ◀ Prev
            </button>
            <p className="text-sm text-stone-600" aria-live="polite">
              {shownStep === 0
                ? 'Starting board'
                : `After move ${shownStep}: ${moves[shownStep - 1]?.primary_word} (+${moves[shownStep - 1]?.move_score})`}
            </p>
            <button
              type="button"
              onClick={() => setStep(Math.min(moves.length, shownStep + 1))}
              disabled={shownStep >= moves.length}
              className="rounded-md border border-stone-300 px-3 py-2 text-sm font-medium disabled:opacity-40"
            >
              Next ▶
            </button>
          </div>

          <ol className="mt-4 space-y-1 text-sm">
            {moves.map((m) => (
              <li
                key={m.move_number}
                className={`rounded-md px-3 py-1.5 ${
                  m.move_number === shownStep ? 'bg-emerald-50 font-medium' : ''
                }`}
              >
                {m.move_number}. {m.primary_word} — {m.move_score} pts
                {m.bingo ? ' · bingo!' : ''}
                {m.cross_words.length > 0
                  ? ` (+ ${m.cross_words.map((c) => c.word).join(', ')})`
                  : ''}
              </li>
            ))}
          </ol>
        </>
      )}
    </section>
  );
}
